'use client';

import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User } from '@auth0/auth0-spa-js';

import { auth0Client, isAuthenticated as checkIsAuthenticated, getUser } from '@/auth/Auth0Client';
import { clearAnonymousUserId } from '@/api/apiUtils';

export interface Auth0ContextType {
    isAuthenticated: boolean;
    isLoading: boolean;
    user: User | undefined;
    loginWithRedirect: () => Promise<void>;
    logout: () => void;
    getAccessToken: () => Promise<string>;
}

const Auth0Context = createContext<Auth0ContextType | undefined>(undefined);

interface Auth0ProviderProps {
    children: ReactNode;
}

export function Auth0Provider({ children }: Auth0ProviderProps) {
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [user, setUser] = useState<User | undefined>(undefined);

    useEffect(() => {
        const initAuth0 = async () => {
            try {
                if (!auth0Client) {
                    setIsLoading(false);
                    return;
                }

                // Handle redirect callback
                if (window.location.search.includes('code=') &&
                    window.location.search.includes('state=')) {
                    try {
                        const client = await auth0Client;
                        const { appState } = await client.handleRedirectCallback();

                        // Redirect the user to the url they were on before the login
                        window.location.replace(
                            appState?.returnTo || window.location.origin + '/app',
                        );
                    } catch (error: any) {
                        console.error('Auth0 callback error', error);
                    }
                }

                // Check if user is authenticated
                const authenticated = await checkIsAuthenticated();
                setIsAuthenticated(authenticated);

                if (authenticated) {
                    const userProfile = await getUser();
                    setUser(userProfile);
                    // Clear anonymous user ID from localStorage after successful login
                    clearAnonymousUserId();
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Auth0 initialization error:', error);
                setIsLoading(false);
            }
        };

        initAuth0();
    }, []);

    const loginWithRedirect = async () => {
        if (auth0Client) {
            await auth0Client.loginWithRedirect({
                authorizationParams: {
                    redirect_uri: window.location.origin + '/app',
                    prompt: 'login', // Forces fresh login so user can choose which Google account to use and won't get stuck if they previously declined permissions during the flow
                },
                appState: {
                    returnTo: window.location.pathname +
                        window.location.search +
                        window.location.hash,
                },
            });
        }
    };

    const logout = () => {
        if (auth0Client) {
            auth0Client.logout({
                logoutParams: {
                    returnTo: window.location.origin + '/app',
                },
            });
        }
    };

    const getAccessToken = async (): Promise<string> => {
        if (!auth0Client) {
            throw new Error('Auth0 client not initialized');
        }
        return await auth0Client.getTokenSilently();
    };

    return (
        <Auth0Context.Provider
            value={{
                isAuthenticated,
                isLoading,
                user,
                loginWithRedirect,
                logout,
                getAccessToken,
            }}>
            {children}
        </Auth0Context.Provider>
    );
}

export function useAuth0(): Auth0ContextType {
    const context = useContext(Auth0Context);
    if (context === undefined) {
        throw new Error('useAuth0 must be used within an Auth0Provider');
    }
    return context;
}
